// ReporteCob.jsx - Panel de Reporte de Cobranzas (estilo DailyPrincipal)
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, TrendingDown, ChevronDown, ChevronRight, Search,
  Users, Tag, Calendar, RefreshCw, Filter, X, FilterX, Download,
  HandCoins, Target, Percent
} from 'lucide-react';
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList
} from 'recharts';
import './DashboardBultos.css';
import DashboardMenuHeader from '../components/CobranzasMenuHeader';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import FullScreenLoader from './FullScreenLoader';
import { calculateWorkingDays } from '../utils/timeUtils';
import * as XLSX from 'xlsx';

/* eslint-disable react-hooks/exhaustive-deps */

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA', 'SIN SEGMENTO'];

const filterLabels = {
  CB_DIA: "Día",
  CLI_REG_VERD: "Región",
  CLI_VEN_DES: "Vendedor",
  CLI_CANAL: "Canal",
  CLI_CANAL_REAL: "Canal Real",
  CLI_SEG_DES: "Segmento",
  CLI_TERRITORIO: "Territorio",
  CLI_PRIN: "Cliente Principal",
  CLI_NOMBRE: "Cliente",
  CLIENTE_ID: "Código Cliente",
  ESPECIALISTA: "Especialista",
  CB_COD_CUENTA: "Cuenta",
  CB_TIPO_ORIGEN: "Origen",
  CB_ANULADO: "Estado",
  CB_TIPO: "Tipo Mov.",
  CB_ORIGEN: "Origen Mov.",
  CB_NRO_DOCUM: "Nro. Documento",
  CB_NRO_COBRO: "Nro. Cobro",
};

const INITIAL_FILTERS = {
  CB_DIA: [], CLI_REG_VERD: [], CLI_VEN_DES: [], CLI_CANAL: [], CLI_CANAL_REAL: [],
  CLI_SEG_DES: [], CLI_TERRITORIO: [], CLI_PRIN: [], CLI_NOMBRE: [], CLIENTE_ID: [],
  ESPECIALISTA: [], CB_COD_CUENTA: [], CB_TIPO_ORIGEN: [], CB_ANULADO: [],
  CB_TIPO: [], CB_ORIGEN: [], CB_NRO_DOCUM: [], CB_NRO_COBRO: [],
};

const nombresMeses = [
  "ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO",
  "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"
];

// ✅ Mapeo mes nombre → número (para ordenar cronológicamente)
const MES_A_NUM = {
  ENERO: 1, FEBRERO: 2, MARZO: 3, ABRIL: 4, MAYO: 5, JUNIO: 6,
  JULIO: 7, AGOSTO: 8, SEPTIEMBRE: 9, OCTUBRE: 10, NOVIEMBRE: 11, DICIEMBRE: 12,
};

// ─── Formateo ───
const parseNumericES = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const s = String(value).trim();
  if (!s) return 0;
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasComma && !hasDot) return Number(s.replace(/\./g, '').replace(',', '.'));
  return Number(s.replace(/,/g, ''));
};

const formatNumber = (num, decimals = 0) => {
  const n = parseNumericES(num);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('es-VE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatUSD = (num) => `$${formatNumber(num, 2)}`;
const formatK = (num) => {
  const n = Number(num || 0);
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return Math.round(n);
};

const getSemaforoColor = (p) => {
  const val = Number(p || 0);
  if (val < 80) return '#8b0000';
  if (val < 90) return '#ff4d4d';
  if (val < 100) return '#ffcc00';
  return '#00ba52';
};

const COLORS = ['#1a2c4e', '#0068b3', '#00ba52', '#4f8cff', '#00e6b8', '#334767', '#8b5cf6', '#f59e0b'];

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.03) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 12;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x} y={y} fill="#cbd5e1" fontSize="10" fontWeight="600"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

// Grid de columnas
const AC_GRID = '2.8fr 0.7fr 0.5fr 1.6fr 0.9fr 0.9fr 0.65fr 0.4fr';

// ✅ Estilo para texto que envuelve (sin ellipsis)
const WRAP_STYLE = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  lineHeight: 1.25,
};


// ══════════════════════════════════════════════════════════════
// MultiSelectFilter
// ══════════════════════════════════════════════════════════════
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues = [], onChange, onClearPill }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? (selectedValues.length === 1
        ? (options.find(opt => (opt.value || opt) === selectedValues[0])?.label ?? selectedValues[0])
        : `${selectedValues.length} Selec.`)
      : 'Todos';

  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt =>
    String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 200);

  const areAllSelected = filteredOptions.every(opt =>
    selectedValues.includes(opt.value || opt)
  );

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = selectedValues.filter(val =>
        !filteredOptions.some(opt => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      onChange(name, filteredOptions.map(opt => opt.value || opt));
    }
  };

  const handleSingleToggle = (optValue) => {
    const newVals = selectedValues.includes(optValue)
      ? selectedValues.filter(v => v !== optValue)
      : [...selectedValues, optValue];
    onChange(name, newVals);
  };

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input
                type="text" placeholder="Buscar..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} autoFocus
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input type="checkbox" checked={areAllSelected} onChange={handleSelectAll} />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(optValue)}
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onClearPill?.(name)}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════
const ReporteCob = () => {
  const {
    cobranzasQueryKpis,
    cobranzasQueryResumenRegion,
    cobranzasQueryTrend,
    cobranzasQueryTopClientes,
    getCobranzasFilters,
    loading: apiLoading,
  } = useQuery();

  const { profile } = useAuth();

  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [filtersReady, setFiltersReady] = useState(false);
  const filtersInitialized = useRef(false);

  // ─── KPIs ───
  const [kpis, setKpis] = useState({
    total_cobrado_usd: 0,
    total_cuota_usd: 0,
    pct_cumplimiento: 0,
    clientes_unicos: 0,
    total_movimientos: 0,
    movimientos_anulados: 0,
  });

  // ─── Datos ───
  const [trendData, setTrendData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [topClientesData, setTopClientesData] = useState([]);

  // ─── Acordeón ───
  const [expandedRegiones, setExpandedRegiones] = useState({});
  const [expandedAsesores, setExpandedAsesores] = useState({});

  // ─── Filtros ───
  const currentYear = new Date().getFullYear();
  const currentMonthName = nombresMeses[new Date().getMonth()];

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableOptions, setAvailableOptions] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedAnios, setSelectedAnios] = useState([currentYear]);
  const [selectedMeses, setSelectedMeses] = useState([currentMonthName]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [viewType, setViewType] = useState('mensual');

  // ─── Working days ───
  const [workDayMetrics, setWorkDayMetrics] = useState({ total: 0, elapsed: 0, percentage: 0 });

  const requestRefs = useRef({ kpis: 0, trend: 0, region: 0, top: 0 });


  // ══════════════════════════════════════════════════════════════
  // WORKING DAYS
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    const metrics = calculateWorkingDays(selectedMeses, selectedAnios, nombresMeses);
    setWorkDayMetrics(metrics);
  }, [selectedMeses, selectedAnios]);


  // ══════════════════════════════════════════════════════════════
  // CONFIG INICIAL
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    const loadConfig = async () => {
      if (filtersInitialized.current) return;
      setLocalLoading(true);
      try {
        const config = await getCobranzasFilters();
        if (!config) throw new Error("Configuración de cobranzas no disponible.");

        const fullOptions = {};
        Object.keys(INITIAL_FILTERS).forEach(key => {
          const raw = config.availableOptions?.[key];
          if (Array.isArray(raw)) {
            fullOptions[key] = raw.map(item =>
              typeof item === 'string' ? item : (item?.value || item?.label || '')
            ).filter(Boolean);
          } else {
            fullOptions[key] = [];
          }
        });

        if (!fullOptions.CB_DIA?.length) {
          fullOptions.CB_DIA = Array.from({ length: 31 }, (_, i) => String(i + 1));
        } else {
          fullOptions.CB_DIA = [...fullOptions.CB_DIA].sort((a, b) => Number(a) - Number(b));
        }

        setAvailableOptions(fullOptions);
        setAvailableYears(config.availableYears || [currentYear]);
        const allSegments = fullOptions.CLI_SEG_DES || [];
        const prefilteredSegments = allSegments
          .map(opt => opt?.value || opt)
          .filter(Boolean)
          .filter(segment =>
            !excludedSegments.some(ex => String(segment).toUpperCase().includes(ex.toUpperCase()))
          );

        setFilters({
          ...INITIAL_FILTERS,
          CLI_SEG_DES: prefilteredSegments,
        });

        if (config.currentYear) setSelectedAnios([config.currentYear]);
        else if (config.availableYears?.[0]) setSelectedAnios([config.availableYears[0]]);

        const dataMonth = config.availableOptions?.COB_MES?.find?.(m => m === currentMonthName);
        setSelectedMeses(dataMonth ? [dataMonth] : [currentMonthName]);

        filtersInitialized.current = true;
        setFiltersReady(true);
      } catch (err) {
        console.error('Error cargando config cobranzas:', err);
        filtersInitialized.current = true;
        setFiltersReady(true);
      } finally {
        setLocalLoading(false);
      }
    };
    loadConfig();
  }, []);


  // ══════════════════════════════════════════════════════════════
  // PAYLOAD GENERAL (con Mes + Día + Año)
  // ══════════════════════════════════════════════════════════════
  const buildBasePayload = useCallback(() => {
    const mergedFilters = { ...filters };
    if (!mergedFilters.COB_AÑO?.length && selectedAnios.length) {
      mergedFilters.COB_AÑO = selectedAnios.map(String);
    }
    if (!mergedFilters.COB_MES?.length && selectedMeses.length) {
      mergedFilters.COB_MES = selectedMeses;
    }
    return {
      filters: mergedFilters,
      page_anios: selectedAnios.filter(Boolean),
      page_meses: selectedMeses.filter(Boolean),
      view_type: viewType,
      user_info: profile
        ? { username: profile.username, cargo: profile.cargo }
        : null,
    };
  }, [filters, selectedAnios, selectedMeses, profile, viewType]);


  // ══════════════════════════════════════════════════════════════
  // ✅ PAYLOAD EXCLUSIVO PARA TREND (ignora Mes y Día)
  // ══════════════════════════════════════════════════════════════
  const buildTrendPayload = useCallback(() => {
    const trendFilters = { ...filters };
    delete trendFilters.COB_MES;
    delete trendFilters.CB_DIA;

    if (!trendFilters.COB_AÑO?.length && selectedAnios.length) {
      trendFilters.COB_AÑO = selectedAnios.map(String);
    }

    return {
      filters: trendFilters,
      page_anios: selectedAnios.filter(Boolean),
      page_meses: [],
      view_type: viewType,
      user_info: profile
        ? { username: profile.username, cargo: profile.cargo }
        : null,
    };
  }, [filters, selectedAnios, profile, viewType]);


  // ══════════════════════════════════════════════════════════════
  // FETCHERS
  // ══════════════════════════════════════════════════════════════
  const fetchKpis = useCallback(async () => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.kpis;
    try {
      const res = await cobranzasQueryKpis(buildBasePayload());
      if (reqId !== requestRefs.current.kpis) return;
      setKpis({
        total_cobrado_usd: parseFloat(res?.total_cobrado_usd || 0),
        total_cuota_usd: parseFloat(res?.total_cuota_usd || 0),
        pct_cumplimiento: parseFloat(res?.pct_cumplimiento || 0),
        clientes_unicos: parseInt(res?.clientes_unicos || 0, 10),
        total_movimientos: parseInt(res?.total_movimientos || 0, 10),
        movimientos_anulados: parseInt(res?.movimientos_anulados || 0, 10),
      });
    } catch (e) {
      console.error('❌ Error KPIs cobranzas:', e);
    }
  }, [buildBasePayload, cobranzasQueryKpis, filtersReady]);

  const fetchTrend = useCallback(async () => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.trend;
    try {
      const res = await cobranzasQueryTrend(buildTrendPayload());
      if (reqId !== requestRefs.current.trend) return;
      setTrendData(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('❌ Error trend:', e);
      setTrendData([]);
    }
  }, [buildTrendPayload, cobranzasQueryTrend, filtersReady]);

  const fetchRegion = useCallback(async () => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.region;
    try {
      const res = await cobranzasQueryResumenRegion(buildBasePayload());
      if (reqId !== requestRefs.current.region) return;
      setRegionData(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('❌ Error regiones:', e);
      setRegionData([]);
    }
  }, [buildBasePayload, cobranzasQueryResumenRegion, filtersReady]);

  const fetchTopClientes = useCallback(async () => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.top;
    try {
      const res = await cobranzasQueryTopClientes({ ...buildBasePayload(), limit: 100 });
      if (reqId !== requestRefs.current.top) return;
      setTopClientesData(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('❌ Error top clientes:', e);
      setTopClientesData([]);
    }
  }, [buildBasePayload, cobranzasQueryTopClientes, filtersReady]);


  // ══════════════════════════════════════════════════════════════
  // EFFECT
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!filtersReady) return;
    const t = setTimeout(async () => {
      setQuerying(true);
      try {
        await Promise.all([fetchKpis(), fetchTrend(), fetchRegion(), fetchTopClientes()]);
      } finally {
        setQuerying(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [filters, selectedAnios, selectedMeses, viewType, filtersReady,
    fetchKpis, fetchTrend, fetchRegion, fetchTopClientes]);


  // ══════════════════════════════════════════════════════════════
  // HANDLERS
  // ══════════════════════════════════════════════════════════════
  const handleRefresh = useCallback(async () => {
    setQuerying(true);
    try {
      await Promise.all([fetchKpis(), fetchTrend(), fetchRegion(), fetchTopClientes()]);
    } finally {
      setQuerying(false);
    }
  }, [fetchKpis, fetchTrend, fetchRegion, fetchTopClientes]);

  const handleResetFilters = useCallback(() => {
    const allSegments = availableOptions.CLI_SEG_DES || [];
    const prefilteredSegments = allSegments
      .map(opt => opt?.value || opt)
      .filter(Boolean)
      .filter(segment =>
        !excludedSegments.some(ex => String(segment).toUpperCase().includes(ex.toUpperCase()))
      );

    setFilters({
      ...INITIAL_FILTERS,
      CLI_SEG_DES: prefilteredSegments,
    });
  }, [availableOptions]);

  const handleClearSingleFilter = useCallback((name) => {
    setFilters(prev => ({ ...prev, [name]: [] }));
  }, []);

  const toggleRegion = (regionKey) => {
    setExpandedRegiones(prev => ({ ...prev, [regionKey]: !prev[regionKey] }));
  };
  const toggleAsesor = (asesorKey) => {
    setExpandedAsesores(prev => ({ ...prev, [asesorKey]: !prev[asesorKey] }));
  };


  // ══════════════════════════════════════════════════════════════
  // EXPORT EXCEL
  // ══════════════════════════════════════════════════════════════
  const exportToExcel = (data, filename, type = 'generic') => {
    let rows = [];
    if (type === 'generic') {
      rows = data.map(item => ({
        Nombre: item.especialista || item.region || item.cli_nombre || item.asesor || '',
        Cobrado_USD: Math.round(item.cobrado_usd || 0),
        Cuota_USD: Math.round(item.cuota_usd || 0),
        Cumplimiento_Pct: Math.round(item.pct_cumplimiento || 0),
      }));
    } else if (type === 'clientes') {
      rows = data.map(item => ({
        'Región': item.region || '',
        'Asesor': item.asesor || '',
        'Cliente Principal': item.cli_prin || item.cli_nombre || '',
        'Cliente ID': item.CLIENTE_ID || '',
        'Descripción': item.cli_nombre || '',
        'Cobrado USD': Math.round(item.cobrado_usd || 0),
        'Cuota USD': Math.round(item.cuota_usd || 0),
        '% Efectividad': Math.round(item.pct_cumplimiento || 0),
        'Movimientos': item.movimientos || 0,
      }));
    } else if (type === 'trend') {
      rows = data.map(item => ({
        Periodo: item.name || '',
        Cobrado_USD: Math.round(item.cobrado || 0),
        Cuota_USD: Math.round(item.cuota || 0),
        Logro_Pct: Math.round(item.logro || 0),
      }));
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${filename}_${new Date().getTime()}.xlsx`);
  };


  // ══════════════════════════════════════════════════════════════
  // DATOS DERIVADOS
  // ══════════════════════════════════════════════════════════════
  // ✅ Trend ordenado cronológicamente (Año ASC → Mes ASC)
  const chartData = useMemo(() => {
    return trendData
      .map(item => {
        const name = item.name || '';

        // Extraer año: "SEPTIEMBRE 2026" → 2026 | "2026" → 2026
        const matchAnio = name.match(/(\d{4})/);
        const anio = matchAnio ? parseInt(matchAnio[1], 10) : 0;

        // Extraer número de mes si el nombre lo contiene
        const nombreUpper = name.toUpperCase();
        let mesNum = 0;
        for (const [mesNombre, num] of Object.entries(MES_A_NUM)) {
          if (nombreUpper.includes(mesNombre)) {
            mesNum = num;
            break;
          }
        }

        return {
          name,
          Cobrado: Number(item.cobrado || 0),
          Cuota: Number(item.cuota || 0),
          Logro: Number(item.logro || 0),
          _anio: anio,
          _mes: mesNum,
        };
      })
      .filter(item => item.Cobrado > 0 || item.Cuota > 0)
      .sort((a, b) => {
        if (a._anio !== b._anio) return a._anio - b._anio;   // Año ASC
        return a._mes - b._mes;                              // Mes ASC
      });
  }, [trendData]);

  const pieTopClientesData = (() => {
    return [...(topClientesData || [])]
      .sort((a, b) => Number(b.cobrado_usd || 0) - Number(a.cobrado_usd || 0))
      .slice(0, 5)
      .map(r => ({
        name: r.cli_nombre || r.CLIENTE_ID || 'SIN NOMBRE',
        value: Number(r.cobrado_usd || 0),
      }))
      .filter(x => x.value > 0);
  })();

  const barRegionData = (() => {
    const map = {};
    (regionData || []).forEach(r => {
      const reg = r.region || 'SIN REGIÓN';
      map[reg] = (map[reg] || 0) + Number(r.cobrado_usd || 0);
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  })();

  // Árbol jerárquico Región → Asesor → Clientes
  const arbolRegiones = useMemo(() => {
    const regiones = {};

    (topClientesData || []).forEach(item => {
      const region = item.region || 'SIN REGIÓN';
      const asesor = item.asesor || 'SIN ASESOR';

      if (!regiones[region]) {
        regiones[region] = {
          region, cobrado_usd: 0, cuota_usd: 0,
          clientes: 0, movimientos: 0, asesores: {},
        };
      }
      const rNode = regiones[region];
      const cobrado = Number(item.cobrado_usd || 0);
      const cuota = Number(item.cuota_usd || 0);
      const movs = Number(item.movimientos || 0);

      rNode.cobrado_usd += cobrado;
      rNode.cuota_usd += cuota;
      rNode.movimientos += movs;
      rNode.clientes += 1;

      if (!rNode.asesores[asesor]) {
        rNode.asesores[asesor] = {
          asesor, cobrado_usd: 0, cuota_usd: 0,
          clientes: 0, movimientos: 0, clientes_list: [],
        };
      }
      const aNode = rNode.asesores[asesor];
      aNode.cobrado_usd += cobrado;
      aNode.cuota_usd += cuota;
      aNode.movimientos += movs;
      aNode.clientes += 1;
      aNode.clientes_list.push(item);
    });

    return Object.values(regiones).map(r => {
      const pct_r = r.cuota_usd > 0 ? (r.cobrado_usd / r.cuota_usd) * 100 : 0;
      const asesoresArr = Object.values(r.asesores).map(a => {
        const pct_a = a.cuota_usd > 0 ? (a.cobrado_usd / a.cuota_usd) * 100 : 0;
        const clientesSorted = [...a.clientes_list].sort(
          (x, y) => Number(y.cobrado_usd || 0) - Number(x.cobrado_usd || 0)
        );
        return { ...a, pct_cumplimiento: pct_a, clientes_list: clientesSorted };
      }).sort((x, y) => y.cobrado_usd - x.cobrado_usd);

      return { ...r, pct_cumplimiento: pct_r, asesores_list: asesoresArr };
    }).sort((x, y) => y.cobrado_usd - x.cobrado_usd);
  }, [topClientesData]);

  const bultosCumplimientoGlobal = useMemo(() => {
    const totalCuota = arbolRegiones.reduce((s, r) => s + Number(r.cuota_usd || 0), 0);
    const totalCobrado = kpis.total_cobrado_usd || 0;
    return totalCuota > 0 ? (totalCobrado / totalCuota) * 100 : 0;
  }, [arbolRegiones, kpis.total_cobrado_usd]);


  // ══════════════════════════════════════════════════════════════
  // 3 TARJETAS KPI
  // ══════════════════════════════════════════════════════════════
  const cumplimientoColor = getSemaforoColor(kpis.pct_cumplimiento);

  const kpiCards = [
    {
      key: 'cobrado',
      icon: <HandCoins size={22} color="white" />,
      label: 'Total Cobrado',
      value: formatUSD(kpis.total_cobrado_usd),
      sublabel: `${formatNumber(kpis.total_movimientos)} movs · ${formatNumber(kpis.clientes_unicos)} clientes`,
      color: '#00ba52',
      textColor: null,
    },
    {
      key: 'cuota',
      icon: <Target size={22} color="white" />,
      label: 'Cuota Asignada',
      value: formatUSD(kpis.total_cuota_usd),
      sublabel: kpis.total_cuota_usd > 0 ? 'Meta del período' : 'Sin cuota cargada',
      color: '#3b82f6',
      textColor: null,
    },
    {
      key: 'cumplimiento',
      icon: <Percent size={22} color="white" />,
      label: '% Cumplimiento',
      value: `${formatNumber(kpis.pct_cumplimiento, 1)}%`,
      sublabel: kpis.total_cuota_usd > 0
        ? `Faltan ${formatUSD(Math.max(0, kpis.total_cuota_usd - kpis.total_cobrado_usd))}`
        : 'Pendiente cuota',
      color: cumplimientoColor,
      textColor: cumplimientoColor,
    },
  ];


  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };


  // ══════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <motion.div
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: "easeInOut" }}
    >
      <DashboardMenuHeader />

      {(apiLoading || localLoading) && !filtersReady && (
        <FullScreenLoader message="Cargando datos de cobranzas..." />
      )}

      {querying && <div className="querying-overlay active" />}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: "easeOut" }}
      >
        {/* ═══ HEADER ═══ */}
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Reporte de Cobranzas</h1>
            <div className="title-underline"></div>
          </div>

          <div className="date-selectors">
            <button className="refresh-button" onClick={handleRefresh} disabled={querying}>
              <RefreshCw size={16} className={querying ? "spin-anim" : ""} />
            </button>

            <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
              <Filter size={16} />
              <span>Filtros</span>
            </button>

            <div className="switch-toggle-group">
              <button
                className={`switch-toggle-btn ${viewType === 'mensual' ? 'active' : ''}`}
                onClick={() => setViewType('mensual')}
              >
                Mensual
              </button>
              <button
                className={`switch-toggle-btn ${viewType === 'anual' ? 'active' : ''}`}
                onClick={() => setViewType('anual')}
              >
                Anual
              </button>
            </div>

            <div className="date-filters-group">
              <MultiSelectFilter
                label="Día" name="CB_DIA" icon={Calendar}
                options={(availableOptions.CB_DIA || []).map(d => ({ value: String(d), label: String(d) }))}
                selectedValues={filters.CB_DIA || []}
                onChange={(name, vals) => setFilters(prev => ({ ...prev, [name]: vals }))}
                onClearPill={handleClearSingleFilter}
              />
              <MultiSelectFilter
                label="Mes" name="meses" icon={Calendar}
                options={nombresMeses.map(m => ({ value: m, label: m }))}
                selectedValues={selectedMeses}
                onChange={(_, vals) => setSelectedMeses(vals)}
              />
              <MultiSelectFilter
                label="Año" name="anios" icon={Calendar}
                options={availableYears.map(y => ({ value: String(y), label: String(y) }))}
                selectedValues={selectedAnios.map(String)}
                onChange={(_, vals) => setSelectedAnios(vals.map(v => Number(v)).filter(n => Number.isFinite(n)))}
              />
            </div>
          </div>
        </header>

        {/* ═══ WORKING DAYS BAR ═══ */}
        {(() => {
          const timeVsSalesDiff = bultosCumplimientoGlobal - Number(workDayMetrics.percentage);
          return (
            <div className="working-days-bar">
              <div className="wd-item"><span>Días Hábiles:</span> <strong>{workDayMetrics.total}</strong></div>
              <div className="wd-item"><span>Transcurridos:</span> <strong>{workDayMetrics.elapsed}</strong></div>
              <div className="wd-item"><span>Avance:</span> <strong>{workDayMetrics.percentage}%</strong></div>
              <div className="wd-separator"></div>
              <div className={`wd-item variation-item ${timeVsSalesDiff >= 0 ? 'positive' : 'negative'}`}>
                {timeVsSalesDiff >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <strong>{timeVsSalesDiff.toFixed(1)}%</strong>
                <span className="wd-label">(vs Avance)</span>
              </div>
              <div className="wd-separator"></div>
              <div className="wd-item">
                <span>Período:</span> <strong>{selectedMeses.join(', ')} {selectedAnios.join(', ')}</strong>
              </div>
              {filters.CB_DIA?.length > 0 && (
                <>
                  <div className="wd-separator"></div>
                  <div className="wd-item">
                    <span>Días:</span> <strong>{filters.CB_DIA.join(', ')}</strong>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* ═══ SIDEBAR FILTROS ═══ */}
        <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros de Cobranzas</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters}>
                <FilterX size={16} />
              </button>
              <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {Object.keys(filters).map((key) => (
              <MultiSelectFilter
                key={key}
                label={filterLabels[key] || key}
                name={key}
                icon={key.includes('CLI') || key === 'ESPECIALISTA' ? Users : Tag}
                options={key === 'CB_DIA'
                  ? (availableOptions.CB_DIA || []).map(d => ({ value: String(d), label: String(d) }))
                  : (availableOptions[key] || [])}
                selectedValues={filters[key] || []}
                onChange={(name, vals) => setFilters(prev => ({ ...prev, [name]: vals }))}
                onClearPill={handleClearSingleFilter}
              />
            ))}
            <button className="filter-pill-button reset-filters" onClick={handleResetFilters}>
              <FilterX size={12} className="pill-icon" />
              <span className="pill-label">Limpiar</span>
            </button>
          </div>
        </div>
        {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

        {/* ═══ 3 CARDS ═══ */}
        <div
          className="dashboard-indicators"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 12,
          }}
        >
          {kpiCards.map(card => (
            <div key={card.key} className="indicator-card v-real">
              <div className="card-content">
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 44, height: 44, borderRadius: '50%',
                  background: card.color, marginBottom: 8,
                }}>
                  {card.icon}
                </div>
                <span className="indicator-label">{card.label}</span>
                <div className="indicator-row">
                  <span
                    className="indicator-value"
                    style={card.textColor ? { color: card.textColor } : undefined}
                  >
                    {card.value}
                  </span>
                </div>
                {card.sublabel && (
                  <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>{card.sublabel}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ═══ FILA 1 · TREND + PIE TOP 5 CLIENTES ═══ */}
        <div className="charts-grid main-row" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s' }}>
          <div className="chart-container">
            <h3 className="chart-title">
              Evolución {viewType === 'anual' ? 'Anual' : 'Mensual'} de Cobranzas
              <span style={{ marginLeft: 8, fontSize: '0.65rem', opacity: 0.55, fontWeight: 400 }}>
                (ignora filtro de Mes/Día)
              </span>
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={chartData} margin={{ top: 20, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff20" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 8, fill: '#fff' }} />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tickFormatter={formatK} tick={{ fontSize: 9, fill: '#fff' }} />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={false} width={0} domain={[0, (max) => Math.max(max, 100) * 1.2]} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', borderRadius: 8, border: 'none', color: '#333' }}
                  formatter={(value, name) => name === 'Logro' ? `${value}%` : formatUSD(value)}
                />
                <Legend
                  verticalAlign="bottom" height={16} iconType="circle"
                  wrapperStyle={{
                    fontSize: '10px', backgroundColor: 'rgba(255,255,255,0.85)',
                    borderRadius: '12px', padding: '5px', width: '90%',
                    margin: '0 auto', left: '5%', bottom: 0,
                    display: 'flex', justifyContent: 'center', color: '#fff',
                  }}
                />
                <Bar yAxisId="left" dataKey="Cuota" fill="#3c4b68" radius={[2, 2, 0, 0]} barSize={12} />
                <Bar yAxisId="left" dataKey="Cobrado" fill="#0068b3" radius={[2, 2, 0, 0]} barSize={12}>
                  <LabelList dataKey="Cobrado" position="top" formatter={formatK} style={{ fontSize: '9px', fill: '#fff' }} />
                </Bar>
                <Line yAxisId="right" type="monotone" dataKey="Logro" stroke="#00ba52" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList dataKey="Logro" position="top" formatter={(v) => `${v}%`} style={{ fontSize: '9px', fill: '#00ba52', fontWeight: 'bold' }} />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container">
            <h3 className="chart-title">Top 5 Clientes por Cobro</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieTopClientesData}
                  cx="50%" cy="45%"
                  innerRadius={40} outerRadius={65}
                  paddingAngle={4} dataKey="value"
                  labelLine={false} label={renderCustomizedLabel}
                >
                  {pieTopClientesData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#fff" strokeWidth={0} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatUSD(value)} />
                <Legend
                  verticalAlign="bottom" height={30} iconType="circle"
                  wrapperStyle={{
                    fontSize: '8px', backgroundColor: 'rgba(255,255,255,0.85)',
                    borderRadius: '12px', padding: '5px', width: '90%',
                    margin: '0 auto', left: '5%', bottom: 0,
                    display: 'flex', justifyContent: 'center', color: '#fff',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ═══ FILA 2 · BAR REGIÓN ═══ */}
        <div className="chart-container mini" style={{ marginTop: '15px' }}>
          <h3 className="chart-title">Cobrado por Región</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={barRegionData} layout="vertical" margin={{ top: 5, right: 50, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={false} stroke="#ffffff10" />
              <XAxis type="number" hide />
              <YAxis
                dataKey="name" type="category" axisLine={false} tickLine={false}
                tick={{ fontSize: 10, fill: '#fff' }} width={100}
              />
              <Tooltip cursor={{ fill: 'transparent' }} formatter={(value) => formatUSD(value)} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                {barRegionData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
                <LabelList dataKey="value" position="right" formatter={(v) => formatK(v)} style={{ fill: '#cbd5e1', fontSize: 10, fontWeight: 'bold' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ═══ FILA 3 · TABLA ACORDEÓN ═══ */}
        <div className="detail-table-container2" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s' }}>
          <div className="chart-container mini table-fixed-height" style={{ flex: 2 }}>
            <div className="table-header-container">
              <h3 className="chart-title">
                Detalle por Región · Asesor · Cliente
                <span style={{ marginLeft: 8, fontSize: '0.7rem', opacity: 0.6 }}>
                  ({arbolRegiones.length} regiones)
                </span>
              </h3>
              <button
                className="export-table-btn"
                onClick={() => exportToExcel(topClientesData, 'Clientes_Cobranzas', 'clientes')}
              >
                <Download size={12} />
              </button>
            </div>

            <div className="minimal-table-wrapper">
              {/* HEADER */}
              <div className="table-header5" style={{ gridTemplateColumns: AC_GRID }}>
                <div className="col-name" style={WRAP_STYLE}>Región / Asesor / Cliente</div>
                <div className="col-data">Cliente ID</div>
                <div className="col-data">Movs.</div>
                <div className="col-name" style={WRAP_STYLE}>Descripción</div>
                <div className="col-data">Cuota Cobro</div>
                <div className="col-data">Cobrado</div>
                <div className="col-data">% Efec.</div>
                <div className="col-status">Est.</div>
              </div>

              {/* BODY */}
              <div className="table-body5">
                {arbolRegiones.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', opacity: 0.5 }}>
                    Sin datos disponibles
                  </div>
                )}

                {arbolRegiones.map((regionNode) => {
                  const regionKey = `R:${regionNode.region}`;
                  const isRegionOpen = !!expandedRegiones[regionKey];
                  const efecR = Number(regionNode.pct_cumplimiento || 0);

                  return (
                    <React.Fragment key={regionKey}>
                      {/* NIVEL 1 · REGIÓN */}
                      <div
                        className="table-row5 advisor-row"
                        style={{ gridTemplateColumns: AC_GRID, fontWeight: 700 }}
                        onClick={() => toggleRegion(regionKey)}
                      >
                        <div
                          className="col-name"
                          style={{ display: 'flex', alignItems: 'flex-start', gap: 6, ...WRAP_STYLE }}
                        >
                          <span style={{ flexShrink: 0, marginTop: 2 }}>
                            {isRegionOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </span>
                          <span>
                            {regionNode.region}
                            <span style={{ fontSize: '0.65rem', opacity: 0.65, marginLeft: 6 }}>
                              ({regionNode.asesores_list.length} asesores · {regionNode.clientes} clientes)
                            </span>
                          </span>
                        </div>
                        <div className="col-data">—</div>
                        <div className="col-data">{regionNode.movimientos}</div>
                        <div className="col-name">—</div>
                        <div className="col-data">{formatUSD(regionNode.cuota_usd)}</div>
                        <div className="col-data">{formatUSD(regionNode.cobrado_usd)}</div>
                        <div className="col-data bold">{efecR.toFixed(1)}%</div>
                        <div className="col-status">
                          <div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecR) }} />
                        </div>
                      </div>

                      {/* NIVEL 2 · ASESORES */}
                      {isRegionOpen && regionNode.asesores_list.map((asesorNode) => {
                        const asesorKey = `${regionKey}|A:${asesorNode.asesor}`;
                        const isAsesorOpen = !!expandedAsesores[asesorKey];
                        const efecA = Number(asesorNode.pct_cumplimiento || 0);

                        return (
                          <React.Fragment key={asesorKey}>
                            <div
                              className="table-row5 advisor-row"
                              style={{
                                gridTemplateColumns: AC_GRID,
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                fontWeight: 600,
                              }}
                              onClick={() => toggleAsesor(asesorKey)}
                            >
                              <div
                                className="col-name"
                                style={{
                                  paddingLeft: '20px',
                                  display: 'flex', alignItems: 'flex-start', gap: 6,
                                  ...WRAP_STYLE,
                                }}
                              >
                                <span style={{ flexShrink: 0, marginTop: 2 }}>
                                  {isAsesorOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </span>
                                <span>
                                  {asesorNode.asesor}
                                  <span style={{ fontSize: '0.65rem', opacity: 0.65, marginLeft: 6 }}>
                                    ({asesorNode.clientes} clientes)
                                  </span>
                                </span>
                              </div>
                              <div className="col-data">—</div>
                              <div className="col-data">{asesorNode.movimientos}</div>
                              <div className="col-name">—</div>
                              <div className="col-data">{formatUSD(asesorNode.cuota_usd)}</div>
                              <div className="col-data">{formatUSD(asesorNode.cobrado_usd)}</div>
                              <div className="col-data bold">{efecA.toFixed(1)}%</div>
                              <div className="col-status">
                                <div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecA) }} />
                              </div>
                            </div>

                            {/* NIVEL 3 · CLIENTES */}
                            {isAsesorOpen && asesorNode.clientes_list.map((cli, cIdx) => {
                              const efecC = Number(cli.pct_cumplimiento || 0);
                              return (
                                <div
                                  className="table-row5"
                                  key={`${asesorKey}|C:${cli.CLIENTE_ID || cIdx}`}
                                  style={{
                                    gridTemplateColumns: AC_GRID,
                                    backgroundColor: 'rgba(0,0,0,0.18)',
                                    fontSize: '0.72rem',
                                  }}
                                >
                                  <div
                                    className="col-name"
                                    style={{ paddingLeft: '40px', ...WRAP_STYLE }}
                                    title={cli.cli_prin || cli.cli_nombre}
                                  >
                                    {cli.cli_prin || cli.cli_nombre || 'SIN CLIENTE PRINCIPAL'}
                                  </div>
                                  <div className="col-data">{cli.CLIENTE_ID || '---'}</div>
                                  <div className="col-data">{cli.movimientos || 0}</div>
                                  <div
                                    className="col-name"
                                    style={WRAP_STYLE}
                                    title={cli.cli_nombre}
                                  >
                                    {cli.cli_nombre || '---'}
                                  </div>
                                  <div className="col-data">{formatUSD(cli.cuota_usd)}</div>
                                  <div className="col-data">{formatUSD(cli.cobrado_usd)}</div>
                                  <div className="col-data">{efecC.toFixed(1)}%</div>
                                  <div className="col-status">
                                    <div
                                      className="status-dot"
                                      style={{ width: 6, height: 6, backgroundColor: getSemaforoColor(efecC) }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* FOOTER */}
              {arbolRegiones.length > 0 && (
                <div className="table-footer5" style={{ gridTemplateColumns: AC_GRID }}>
                  <div className="col-name" style={{ gridColumn: 'span 4' }}>
                    TOTALES GENERALES
                  </div>
                  <div className="col-data">{formatUSD(kpis.total_cobrado_usd)}</div>
                  <div className="col-data">
                    {formatUSD(arbolRegiones.reduce((s, r) => s + Number(r.cuota_usd || 0), 0))}
                  </div>
                  <div className="col-data bold">
                    {kpis.total_cobrado_usd > 0
                      ? ((kpis.total_cobrado_usd /
                        Math.max(1, arbolRegiones.reduce((s, r) => s + Number(r.cuota_usd || 0), 0))) * 100).toFixed(1)
                      : 0}%
                  </div>
                  <div className="col-status">
                    <div className="status-dot" style={{ backgroundColor: getSemaforoColor(100) }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <footer className="status-footer">
          {querying
            ? "🔄 SINCRONIZANDO COBRANZAS..."
            : `💰 REPORTE COBRANZAS · ${selectedMeses.join(', ')} ${selectedAnios.join(', ')}${filters.CB_DIA?.length > 0 ? ` · Días: ${filters.CB_DIA.join(', ')}` : ''}`}
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default ReporteCob;